/* az-klubbkod.js — klubbkod och lösenord, delat av lösenordsgrinden
   (snippets/az-klubbgrind.liquid) och inloggningsrutan på startsidan
   (sections/az-klubblogin.liquid). Filen lägger också knappen "Visa lösenord" på
   fälten som har data-az-visa-losen (längst ned).

   Samma hash på båda ställena är poängen: grinden jämför varukorgsattributet
   klubbkod_<handle> med SHA-256 av "<handle>:<lösenordet i gemener>", och
   inloggningsrutan ska skriva exakt det värdet. Två kopior av hashningen
   glider isär; den här filen är den enda. Bara hashen lämnar webbläsaren,
   lösenordet hamnar aldrig i varukorgen eller på ordern (#1005, 2026-10-01). */
(function () {
  'use strict';

  function sha256(text) {
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)).then(function (buf) {
      return Array.prototype.map.call(new Uint8Array(buf), function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
    });
  }

  // "STP-TKD", "stp tkd" och "Stp_Tkd" ska vara samma kod: gemener, utan
  // accenter, bara a-z och 0-9.
  function normalisera(kod) {
    return String(kod || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Skriver klubbens hash som varukorgsattribut, det grinden läser.
  function sparaKod(handle, losenord, cartUpdateUrl) {
    if (!window.crypto || !crypto.subtle) return Promise.reject(new Error('crypto'));
    var nyckel = 'klubbkod_' + handle;
    return sha256(handle + ':' + String(losenord).trim().toLowerCase()).then(function (hash) {
      var attr = {};
      attr[nyckel] = hash;
      // Äldre version: attribut med understreck. Tomt värde tar bort det.
      var gammal = nyckel.replace(/-/g, '_');
      if (gammal !== nyckel) attr[gammal] = '';
      return fetch(cartUpdateUrl || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attr })
      });
    });
  }

  // Koden → butikens sida, utan att någon lista över klubbar finns i sidan.
  // Koden prövas som /pages/klubb/<kod> och sedan /pages/foretag/<kod>. Skiljer
  // sig koden från butikens handle (STPTKD, handle stp-tkd) lägger AZ en
  // URL-omdirigering i Shopify (/pages/klubb/stptkd → /pages/klubb/stp-tkd);
  // den syns inte i sidans källkod. fetch följer omdirigeringen, så den riktiga
  // handlen läses ur svarets slutliga URL. Löser till { typ, handle, url }
  // eller null (404). Nätverksfel och andra statuskoder kastar: det är inte
  // "okänd kod".
  function hittaButik(kod, rotUrl) {
    var norm = normalisera(kod);
    if (!norm) return Promise.resolve(null);
    var rot = (rotUrl || '/').replace(/\/?$/, '/');
    var prov = ['klubb', 'foretag'];
    function nasta(i) {
      if (i >= prov.length) return Promise.resolve(null);
      var url = rot + 'pages/' + prov[i] + '/' + encodeURIComponent(norm);
      return fetch(url, { credentials: 'same-origin', cache: 'no-store' }).then(function (r) {
        if (r.status === 404) return nasta(i + 1);
        if (!r.ok) throw new Error('status ' + r.status);
        var typ = prov[i], handle = norm, slutlig = url;
        if (r.redirected && r.url) {
          // Bara en omdirigering till en annan klubb- eller företagssida räknas.
          var m = /\/pages\/(klubb|foretag)\/([^/?#]+)/.exec(r.url);
          if (!m) return nasta(i + 1);
          typ = m[1]; handle = decodeURIComponent(m[2]); slutlig = r.url;
        }
        return { typ: typ, handle: handle, url: slutlig };
      });
    }
    return nasta(0);
  }

  // Visa lösenordet (2026-10-08): en knapp i fältets högerkant på varje
  // input[data-az-visa-losen]. Knappen skapas här och inte i Liquid, så att den
  // som saknar JavaScript inte får en död knapp. Fältet byter bara type; inget
  // läses eller sparas. Texterna är förval på svenska och kan sättas på fältets
  // formulär (data-visa-losen, data-dolj-losen: inloggningsrutans inställningar).
  // aria-pressed och aria-label byts tillsammans, eftersom "Visa lösenord" med
  // pressed=true annars läses som motsatsen av vad knappen gör.
  var OGA = '<path d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12z"></path><circle cx="12" cy="12" r="3"></circle>';
  var OGA_AV = '<path d="M3 3l18 18"></path><path d="M10.6 6.1A10.7 10.7 0 0 1 12 6c6.5 0 10.5 6 10.5 6a17 17 0 0 1-3.2 3.7M6.6 7.6A17 17 0 0 0 1.5 12S5.5 19 12 19a10 10 0 0 0 4.2-.9"></path><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"></path>';
  function losenordsvisning(falt) {
    if (!falt || falt.getAttribute('data-az-visa-init')) return;
    falt.setAttribute('data-az-visa-init', '1');
    var form = falt.form || falt.closest('form');
    var text = function (namn, forval) { return (form && form.getAttribute('data-' + namn)) || forval; };
    var visa = text('visa-losen', 'Visa lösenord'), dolj = text('dolj-losen', 'Dölj lösenord');
    var omslag = document.createElement('span');
    omslag.className = 'az-losen';
    falt.parentNode.insertBefore(omslag, falt);
    omslag.appendChild(falt);
    var knapp = document.createElement('button');
    knapp.type = 'button';
    knapp.className = 'az-losen__knapp';
    knapp.setAttribute('aria-pressed', 'false');
    knapp.setAttribute('aria-label', visa);
    var svg = function (klass, inre) {
      return '<svg class="az-losen__oga ' + klass + '" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + inre + '</svg>';
    };
    knapp.innerHTML = svg('az-losen__oga--visa', OGA) + svg('az-losen__oga--dolj', OGA_AV);
    knapp.addEventListener('click', function () {
      var visas = falt.type === 'password';
      falt.type = visas ? 'text' : 'password';
      knapp.setAttribute('aria-pressed', String(visas));
      knapp.setAttribute('aria-label', visas ? dolj : visa);
    });
    omslag.appendChild(knapp);
  }
  function startaVisning() {
    Array.prototype.forEach.call(document.querySelectorAll('input[data-az-visa-losen]'), losenordsvisning);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startaVisning);
  else startaVisning();

  window.AZKlubbkod = { sha256: sha256, normalisera: normalisera, sparaKod: sparaKod, hittaButik: hittaButik, losenordsvisning: losenordsvisning };
})();
